import { useCallback, useEffect, useRef, useState } from 'react'
import type { ArtBook, ArtBookMetadata, ArtBookPage } from '../definitions/ArtBook'
import { AGE_OPTIONS, ageRangeFromYears } from '../definitions/Age'
import type { BookCreationInput } from '../definitions/BookCreationInput'
import type { Gender } from '../definitions/Gender'
import { validateBookCreationInput } from '../definitions/BookCreationInput'
import { constants } from '../constants'
import { setupFirebase, signInFirebaseAnonymously } from '../services/firebase'
import { photoService } from '../services/photo'
import { bookService } from '../services/book'
import type { BookThemeSummary, CatalogSampleCharacterId, CatalogSampleResult, CharacterStyleSummary, QueryBookResult } from '../services/book'
import { compressImageFile } from '../utils/compressImage'
import { hasEveryGeneratedPage, queryBookResultToArtBook } from '../utils/queryBookResultToArtBook'
import { BookCover, BookSpread } from '../components/BookMockup'
import { ArrowIcon, Logo, Modal, Spinner } from '../components/SiteShell'
import { WatermarkedImage } from '../components/WatermarkedImage'
import { BOOK_CATEGORIES, getSampleCharacters, getStoreBook, sampleUrl, type BookCategory, type StoreBook } from '../data/books'

type Stage = 'create' | 'preview' | 'customize'
type LoadState = 'idle' | 'loading' | 'success' | 'failed'

const PAGE_GENERATION_START_INTERVAL_MS = 800
const PAGE_GENERATION_TIMEOUT_MS = 210_000
const GENERATION_RECONCILE_INTERVAL_MS = 12_000
const PRINTED_BOOK_PAGE_COUNT = 26
const parsedMaxInFlight = Number.parseInt(import.meta.env.VITE_AI_IMAGE_MAX_IN_FLIGHT || '26', 10)
const PAGE_GENERATION_MAX_IN_FLIGHT = Number.isFinite(parsedMaxInFlight) && parsedMaxInFlight > 0
  ? parsedMaxInFlight
  : 26

function waitFor(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (milliseconds <= 0) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, milliseconds)
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timer)
      reject(new DOMException('Page generation was cancelled', 'AbortError'))
    }, { once: true })
  })
}

const emptyBook: ArtBook = {
  metadata: { name: '', ageRange: 'under-1', gender: 'BOY', userInputImageUrl: '', domain: '', themeId: '', themeVersion: '', themeName: '', themePageCount: 0, themePreviewPageCount: 0, characterStyleId: 'realistic', characterStyleVersion: '', characterStyleName: 'Realistic', requiresCharacterAnchor: false, characterAnchorReady: false },
  cover: { coverTitleTop: '', generatedImageUrl: '', generationUuid: '' },
  pages: [],
  revision: 1,
}

function pageCount(book: ArtBook): number {
  return book.metadata.themePageCount
}

function previewPageCount(book: ArtBook): number {
  return book.metadata.themePreviewPageCount
}

function generatedPageCount(book: ArtBook): number {
  return book.pages.filter(page => Boolean(page.generatedImageUrl)).length
}

function isBookComplete(book: ArtBook): boolean {
  return generatedPageCount(book) === pageCount(book)
}

function generationErrorMessage(error: unknown): string {
  if (!navigator.onLine) return 'You appear to be offline. Reconnect, then try this page again.'
  const message = error instanceof Error ? error.message : ''
  if (/429/.test(message)) return 'The illustration service is busy. Please try this page again in a moment.'
  if (/timeout|timed out/i.test(message) || (error instanceof DOMException && error.name === 'TimeoutError')) return 'This page took longer than expected. We checked the saved results, but it still needs another try.'
  if (/40[03]/.test(message)) return 'The reference photo could not be used for this page. Please try again or contact support.'
  return 'The illustration service couldn’t finish this page. Your completed pages are safe.'
}

function mergeServerResults(current: ArtBook, result: QueryBookResult): ArtBook {
  const generated = new Map(result.pages.map(page => [page.pageNumber, page]))
  return {
    ...current,
    revision: Math.max(current.revision, result.revision),
    cover: result.cover.generatedImageUrl ? result.cover : current.cover,
    pages: current.pages.map(page => {
      const saved = generated.get(page.pageNumber)
      return saved ? { ...saved, generateState: 'success' as const, isActive: saved.isActive ?? page.isActive ?? true } : page
    }),
  }
}

async function withGenerationTimeout<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  parentSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController()
  const abortFromParent = () => controller.abort(parentSignal?.reason)
  parentSignal?.addEventListener('abort', abortFromParent, { once: true })
  const timer = window.setTimeout(() => {
    controller.abort(new DOMException('Image generation timed out', 'TimeoutError'))
  }, PAGE_GENERATION_TIMEOUT_MS)
  try {
    return await operation(controller.signal)
  } finally {
    window.clearTimeout(timer)
    parentSignal?.removeEventListener('abort', abortFromParent)
  }
}

export default function StudioPage() {
  const [stage, setStage] = useState<Stage>('create')
  const [input, setInput] = useState<BookCreationInput>(() => ({
    name: '',
    ageRange: undefined,
    gender: 'BOY',
    imageUrl: undefined,
    themeId: new URLSearchParams(window.location.search).get(constants.searchParams.theme) || 'my-body',
    characterStyleId: 'realistic',
  }))
  const [themes, setThemes] = useState<BookThemeSummary[]>([])
  const [characterStyles, setCharacterStyles] = useState<CharacterStyleSummary[]>([])
  const [book, setBook] = useState<ArtBook>(emptyBook)
  const bookRef = useRef(book)
  const [bookId, setBookId] = useState<string | undefined>(() => (
    new URLSearchParams(window.location.search).get(constants.searchParams.photoBookId) ?? undefined
  ))
  const [busy, setBusy] = useState<LoadState>('idle')
  const [error, setError] = useState('')
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const authReady = useRef(false)
  const generationController = useRef<AbortController | undefined>(undefined)

  useEffect(() => { bookRef.current = book }, [book])

  useEffect(() => {
    if (busy !== 'loading' || !pageCount(book)) return
    const originalTitle = document.title
    document.title = `${generatedPageCount(book)}/${pageCount(book)} pages ready · Lucie's Book`
    return () => { document.title = originalTitle }
  }, [book, busy])

  useEffect(() => {
    if (busy !== 'loading' || !bookId || !generationStartedAt) return
    let active = true
    let checking = false
    const reconcile = async () => {
      if (checking) return
      checking = true
      try {
        const result = await bookService.queryBook({ photoBookId: bookId })
        if (!active) return
        const merged = mergeServerResults(bookRef.current, result)
        bookRef.current = merged
        setBook(merged)
        if (isBookComplete(merged)) {
          setBusy('success')
          setError('')
        }
      } catch (caught) {
        console.warn('Could not refresh saved generation results', caught)
      } finally {
        checking = false
      }
    }
    const timer = window.setInterval(reconcile, GENERATION_RECONCILE_INTERVAL_MS)
    return () => { active = false; window.clearInterval(timer) }
  }, [bookId, busy, generationStartedAt])

  useEffect(() => {
    let active = true
    bookService.listThemes().then(result => {
      if (!active) return
      setThemes(result.themes)
      setCharacterStyles(result.characterStyles)
      setInput(current => {
        const next = { ...current }
        const selectedTheme = result.themes.find(theme => theme.id === current.themeId) || result.themes[0]
        if (selectedTheme && selectedTheme.id !== current.themeId) next.themeId = selectedTheme.id
        next.characterStyleId = selectedTheme?.characterStyleId || 'realistic'
        return next
      })
    }).catch(caught => console.error('Could not load book themes', caught))
    return () => { active = false }
  }, [])

  const ensureAuth = useCallback(async () => {
    if (authReady.current) return
    setupFirebase()
    await signInFirebaseAnonymously()
    authReady.current = true
  }, [])

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get(constants.searchParams.photoBookId)
    if (!id) return
    let active = true
    setBusy('loading')
    ensureAuth().then(() => bookService.queryBook({ photoBookId: id })).then((result) => {
      if (!active) return
      const restored = queryBookResultToArtBook(result)
      setBook(restored)
      setBookId(id)
      setInput({ name: restored.metadata.name, ageRange: restored.metadata.ageYears == null ? restored.metadata.ageRange : ageRangeFromYears(restored.metadata.ageYears), gender: restored.metadata.gender, imageUuid: result.metadata.userInputImageUuid, imageUrl: restored.metadata.userInputImageUrl, toapis: restored.metadata.toapis ?? undefined, imagePath: restored.metadata.userInputImagePath, themeId: restored.metadata.themeId, characterStyleId: restored.metadata.characterStyleId || 'realistic' })
      setStage(generatedPageCount(restored) <= previewPageCount(restored) ? 'preview' : 'customize')
      setBusy('success')
    }).catch(() => {
      if (!active) return
      setBusy('failed')
      setError('We couldn’t reopen this book. Please try the link again.')
    })
    return () => { active = false }
  }, [ensureAuth])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (bookId) url.searchParams.set(constants.searchParams.photoBookId, bookId)
    else url.searchParams.delete(constants.searchParams.photoBookId)
    window.history.replaceState({}, '', url)
  }, [bookId])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (input.themeId) url.searchParams.set(constants.searchParams.theme, input.themeId)
    else url.searchParams.delete(constants.searchParams.theme)
    window.history.replaceState({}, '', url)
  }, [input.themeId])

  const updateServer = useCallback(async (nextBook = bookRef.current, id = bookId) => {
    if (!id) return
    const result = await bookService.updateBook({
      photoBookId: id,
      coverGenerationUuid: nextBook.cover.generationUuid || undefined,
      excludedPageNumbers: nextBook.pages.filter(page => page.isActive === false).map(page => page.pageNumber),
      revision: nextBook.revision,
    })
    const revised = { ...bookRef.current, revision: result.revision }
    bookRef.current = revised
    setBook(revised)
  }, [bookId])

  function commitBook(update: (current: ArtBook) => ArtBook): ArtBook {
    const next = update(bookRef.current)
    bookRef.current = next
    setBook(next)
    return next
  }

  async function ensureCharacterAnchor(id: string) {
    const current = bookRef.current
    if (!current.metadata.requiresCharacterAnchor || current.metadata.characterAnchorReady) return
    const result = await withGenerationTimeout(
      signal => photoService.generateCharacterAnchor({ photoBookId: id }, signal),
      generationController.current?.signal,
    )
    commitBook(book => ({
      ...book,
      revision: Math.max(book.revision, result.revision),
      metadata: { ...book.metadata, characterAnchorReady: true },
    }))
  }

  async function generatePages(from: number, to: number, id?: string) {
    if (!id) throw new Error('A photo book must be created before generating pages')
    await ensureCharacterAnchor(id)
    const pageNumbers = Array.from({ length: to - from + 1 }, (_, index) => from + index)
      .filter(pageNumber => !bookRef.current.pages.some(page => page.pageNumber === pageNumber && page.generatedImageUrl))
    if (!pageNumbers.length) return
    commitBook(current => {
      const pagesByNumber = new Map(current.pages.map(page => [page.pageNumber, page]))
      pageNumbers.forEach(pageNumber => {
        const themePage = current.themePages?.find(page => page.pageNumber === pageNumber)
        pagesByNumber.set(pageNumber, {
          ...(pagesByNumber.get(pageNumber) || {
            pageNumber,
            pageId: themePage?.id,
            pageTitle: themePage?.title,
            generationUuid: '',
            generatedImageUrl: '',
            isActive: true,
          }),
          generateState: 'idle',
          generationError: undefined,
        })
      })
      return { ...current, pages: [...pagesByNumber.values()].sort((left, right) => left.pageNumber - right.pageNumber) }
    })
    const failures: unknown[] = []
    const generatePage = async (pageNumber: number) => {
      commitBook(current => ({ ...current, pages: current.pages.map(item => item.pageNumber === pageNumber ? { ...item, generateState: 'loading' as const, generationError: undefined } : item) }))
      try {
        const result = await withGenerationTimeout(signal => photoService.generateArtBookPage({
          pageNumber,
          photoBookId: id,
          imageBase64: input.imageBase64,
          imageUrl: input.imageUrl,
          toapis: input.toapis,
        }, signal), generationController.current?.signal)
        const page: ArtBookPage = { pageNumber, pageId: result.pageId, pageTitle: result.pageTitle, generateState: 'success', generationUuid: result.generationUuid, generatedImagePath: result.generatedImagePath, generatedImageUrl: result.generatedImageUrl, isActive: true }
        commitBook(current => ({ ...current, revision: Math.max(current.revision, result.revision), cover: pageNumber === 1 ? { ...current.cover, generatedImageUrl: result.generatedImageUrl, generatedImagePath: result.generatedImagePath, generationUuid: result.generationUuid } : current.cover, pages: current.pages.map(item => item.pageNumber === pageNumber ? page : item) }))
      } catch (caught) {
        failures.push(caught)
        commitBook(current => ({ ...current, pages: current.pages.map(item => item.pageNumber === pageNumber && !item.generatedImageUrl ? { ...item, generateState: 'failed' as const, generationError: generationErrorMessage(caught) } : item) }))
      }
    }

    const inFlight = new Set<Promise<void>>()
    let lastStartedAt = 0
    for (const pageNumber of pageNumbers) {
      while (inFlight.size >= PAGE_GENERATION_MAX_IN_FLIGHT) {
        await Promise.race(inFlight)
      }
      const signal = generationController.current?.signal
      if (signal?.aborted) throw new DOMException('Page generation was cancelled', 'AbortError')
      const elapsedSinceLastStart = Date.now() - lastStartedAt
      await waitFor(
        Math.max(0, PAGE_GENERATION_START_INTERVAL_MS - elapsedSinceLastStart),
        signal,
      )
      lastStartedAt = Date.now()
      const task = generatePage(pageNumber)
      inFlight.add(task)
      task.finally(() => inFlight.delete(task))
    }
    await Promise.all(inFlight)
    if (failures.length) {
      // A browser request can time out after the model and storage have already
      // succeeded. Re-read the server record before telling the customer that
      // pages failed; this is read-only and never starts a second paid request.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) await waitFor(5000, generationController.current?.signal)
        const restored = queryBookResultToArtBook(await bookService.queryBook({ photoBookId: id }))
        commitBook(current => ({ ...restored, themePages: current.themePages }))
        if (hasEveryGeneratedPage(restored, pageNumbers)) return
      }
      throw failures[0]
    }
  }

  async function createBook() {
    const validation = validateBookCreationInput(input)
    if (validation) { setError(validation); return }
    setError('')
    setBusy('loading')
    setGenerationStartedAt(Date.now())
    setStage('preview')
    generationController.current?.abort()
    generationController.current = new AbortController()
    const theme = themes.find(item => item.id === input.themeId)
    if (!theme) { setError('Book themes are still loading. Please try again.'); setBusy('failed'); setStage('create'); return }
    const selectedStyle = characterStyles.find(style => style.id === input.characterStyleId)
    if (!selectedStyle) { setError('Character styles are still loading. Please try again.'); setBusy('failed'); setStage('create'); return }
    const metadata: ArtBookMetadata = { toapis: input.toapis, name: input.name.trim(), ageRange: input.ageRange!, gender: input.gender, userInputImageUrl: input.imageUrl!, userInputImagePath: input.imagePath, domain: window.location.host, themeId: theme.id, themeVersion: theme.version, themeName: theme.name, themePageCount: theme.pageCount, themePreviewPageCount: theme.previewPageCount, characterStyleId: selectedStyle.id, characterStyleVersion: selectedStyle.version, characterStyleName: selectedStyle.name, requiresCharacterAnchor: theme.requiresCharacterAnchor, characterAnchorReady: false }
    const coverTitle = theme.coverTitleTemplate.replace(/{{\s*child_name\s*}}/g, metadata.name)
    const initial: ArtBook = { ...emptyBook, metadata, cover: { ...emptyBook.cover, coverTitleTop: coverTitle } }
    setBook(initial)
    bookRef.current = initial
    try {
      await ensureAuth()
      const result = await bookService.createBook({
        themeId: theme.id,
        themeVersion: theme.version,
        characterStyleId: selectedStyle.id,
        characterStyleVersion: selectedStyle.version,
        name: metadata.name,
        gender: metadata.gender,
        ageRange: input.ageRange!,
        referenceImageUuid: input.imageUuid!,
        referenceImagePath: input.imagePath!,
        toapis: input.toapis,
        domain: metadata.domain,
      })
      const authoritativeMetadata: ArtBookMetadata = {
        ...metadata,
        themeId: result.theme.id,
        themeVersion: result.theme.version,
        themeName: result.theme.name,
        themePageCount: result.totalPageCount,
        themePreviewPageCount: result.theme.previewPageCount,
        characterStyleId: result.characterStyle.id,
        characterStyleVersion: result.characterStyle.version,
        characterStyleName: result.characterStyle.name,
        requiresCharacterAnchor: result.theme.requiresCharacterAnchor,
        characterAnchorReady: false,
      }
      const createdBook = { ...initial, metadata: authoritativeMetadata, themePages: result.theme.pages, revision: result.revision }
      setBook(createdBook)
      bookRef.current = createdBook
      setBookId(result.photoBookId)
      await generatePages(1, result.theme.previewPageCount, result.photoBookId)
      setBusy('success')
      setGenerationStartedAt(null)
    } catch (caught) {
      if ((caught as Error).name !== 'AbortError') {
        console.error(caught)
        setBusy('failed')
        setError(generationErrorMessage(caught))
        setGenerationStartedAt(null)
      }
    }
  }

  async function createFullBook() {
    const totalPages = pageCount(book)
    const complete = isBookComplete(book)
    if (complete) { setStage('customize'); return }
    setStage('customize')
    setBusy('loading')
    setGenerationStartedAt(Date.now())
    setError('')
    try {
      await generatePages(1, totalPages, bookId)
      setBusy('success')
      setError('')
      setGenerationStartedAt(null)
    } catch (caught) {
      console.error(caught)
      if (isBookComplete(bookRef.current)) {
        setBusy('success')
        setError('')
        setGenerationStartedAt(null)
        return
      }
      setBusy('failed')
      setError('Some pages didn’t finish. Your completed pages are saved; retry only the pages marked below.')
      setGenerationStartedAt(null)
    }
  }

  async function retryPage(pageNumber: number) {
    if (!bookId || busy === 'loading') return
    setBusy('loading')
    setGenerationStartedAt(Date.now())
    setError('')
    try {
      await generatePages(pageNumber, pageNumber, bookId)
      setBusy(isBookComplete(bookRef.current) ? 'success' : 'failed')
    } catch (caught) {
      console.error(caught)
      setBusy(isBookComplete(bookRef.current) ? 'success' : 'failed')
      if (!isBookComplete(bookRef.current)) setError(`Page ${pageNumber} still needs attention. Your other pages are safe.`)
    } finally {
      setGenerationStartedAt(null)
    }
  }

  function changePage(page: ArtBookPage, changes: Partial<ArtBookPage>, makeCover = false) {
    const updated = { ...page, ...changes }
    const next: ArtBook = {
      ...book,
      cover: makeCover ? { ...book.cover, generatedImageUrl: updated.generatedImageUrl, generatedImagePath: updated.generatedImagePath, generationUuid: updated.generationUuid } : book.cover,
      pages: book.pages.map(item => item.pageNumber === page.pageNumber ? updated : item),
    }
    setBook(next)
    bookRef.current = next
    updateServer(next).catch(console.error)
  }

  function goBack() {
    if (stage === 'customize' && busy !== 'loading') setStage('preview')
    else if (stage === 'preview' && busy !== 'loading') setStage('create')
    else if (stage === 'create') window.location.assign('/')
  }

  return (
    <div className="studio-page">
      <StudioHeader stage={stage} busy={busy} onBack={goBack} />
      <main className="studio-main">
        {stage === 'create' && <CreateStep input={input} setInput={setInput} themes={themes} error={error} setError={setError} onCreate={createBook} />}
        {stage === 'preview' && <PreviewStep book={book} busy={busy} error={error} generationStartedAt={generationStartedAt} onContinue={createFullBook} />}
        {stage === 'customize' && <CustomizeStep book={book} busy={busy} error={error} generationStartedAt={generationStartedAt} onChange={changePage} onRetry={createFullBook} onRetryPage={retryPage} onCheckout={() => setCheckoutOpen(true)} />}
      </main>
      {checkoutOpen && <CheckoutModal bookId={bookId} pageTotal={PRINTED_BOOK_PAGE_COUNT} onClose={() => setCheckoutOpen(false)} />}
    </div>
  )
}

function StudioHeader({ stage, busy, onBack }: { stage: Stage; busy: LoadState; onBack: () => void }) {
  const currentStep = stage === 'create' ? 0 : stage === 'preview' ? 1 : 2
  return (
    <header className="studio-header">
      <div className="studio-header-inner">
        <div className="studio-header-brand">
          <Logo />
        </div>
        <div className="studio-progress" aria-label={`Step ${currentStep + 1} of 3`}>
          {['Details', 'Preview', 'Personalize'].map((label, index) => (
            <div className={`${currentStep >= index ? 'active' : ''} ${currentStep === index ? 'current' : ''}`} key={label} aria-current={currentStep === index ? 'step' : undefined}>
              <span>{currentStep > index ? '✓' : index + 1}</span><strong>{label}</strong>
            </div>
          ))}
        </div>
        <div className="studio-header-actions">
          <div className="studio-assurance"><span aria-hidden="true">♡</span><div><strong>Made with care</strong><small>Your photo stays private</small></div></div>
          <button className="studio-back" onClick={onBack} disabled={busy === 'loading' && stage !== 'create'} aria-label="Go back">
            <ArrowIcon direction="left" /><span>Back</span>
          </button>
        </div>
      </div>
    </header>
  )
}

function CreateStep({ input, setInput, themes, error, setError, onCreate }: { input: BookCreationInput; setInput: (input: BookCreationInput) => void; themes: BookThemeSummary[]; error: string; setError: (value: string) => void; onCreate: () => void }) {
  const [uploading, setUploading] = useState(false)
  const [bookPickerOpen, setBookPickerOpen] = useState(false)
  const [sampleOpen, setSampleOpen] = useState(false)
  const selectedTheme = themes.find(theme => theme.id === input.themeId)
  const selectedBook = getStoreBook(input.themeId)
  const personalizationFields = selectedBook?.personalizationFields || ['photo', 'name', 'gender', 'age']
  function chooseTheme(themeId: string) {
    const theme = themes.find(candidate => candidate.id === themeId)
    setInput({
      ...input,
      themeId,
      characterStyleId: theme?.characterStyleId || 'realistic',
    })
    setBookPickerOpen(false)
  }
  async function pickPhoto(file?: File) {
    if (!file) return
    setUploading(true); setError('')
    try {
      setupFirebase()
      await signInFirebaseAnonymously()
      const data = await compressImageFile(file)
      const result = await photoService.uploadImages({ images: [data] })
      setInput({ ...input, imageBase64: data, toapis: result.results[0]?.toapis, imageUuid: result.results[0]?.imageUuid, imageUrl: result.results[0]?.imageUrl, imagePath: result.results[0]?.imagePath })
    } catch (caught) { console.error(caught); setError('We couldn’t upload that photo. Try a JPG or PNG under 10 MB.') }
    finally { setUploading(false) }
  }
  return (
    <section className="studio-card create-card">
      <div className="studio-title studio-title-left"><span className="eyebrow">Create their book</span><h1>You chose the story.<br /><em>Now make it theirs.</em></h1><p>One lovely photo and a few small details are all we need to create their free preview.</p></div>
      <div className="create-layout">
        <div className="form-panel">
          <div className="panel-heading"><span>3</span><div><strong>Add the little details</strong><small>So every page feels unmistakably theirs</small></div></div>
          {personalizationFields.includes('name') && <><label className="field-label" htmlFor="child-name">Their first name</label><input id="child-name" className="text-input" value={input.name} onChange={event => setInput({ ...input, name: event.target.value })} placeholder="e.g. Lucie" autoComplete="off" /></>}
          {personalizationFields.includes('gender') && <fieldset><legend className="field-label">They are a...</legend><Segmented options={[['GIRL', 'Girl'], ['BOY', 'Boy']]} value={input.gender} onChange={gender => setInput({ ...input, gender: gender as Gender })} /></fieldset>}
          {personalizationFields.includes('age') && <div className="age-field"><label className="field-label" htmlFor="child-age">How old are they?</label><select id="child-age" className="text-input" value={input.ageRange ?? ''} onChange={event => setInput({ ...input, ageRange: event.target.value === '' ? undefined : event.target.value as ArtBookMetadata['ageRange'] })} required><option value="" disabled>Choose their age range</option>{AGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>}
          {personalizationFields.includes('photo') && <div className="photo-tips"><strong>A photo that works beautifully</strong><p>Choose a clear, front-facing photo in natural light, with their full face visible.</p><div><img src="/images/sample-1.png" alt="Good portrait example" /><img src="/images/sample-2.png" alt="Good portrait example" /></div></div>}
        </div>
        <div className="upload-panel">
          <div className="studio-theme-picker">
            <div className="panel-heading theme-picker-heading"><span>1</span><div><strong>Their book</strong><small>You can change it without losing your place</small></div></div>
            <div className="selected-book-summary">
              <div className="selected-book-cover"><img src={selectedBook?.image || `/images/theme-${input.themeId}.jpg`} alt="" /></div>
              <div className="selected-book-copy"><span>{selectedBook ? `${selectedBook.category} · ${selectedBook.age}` : 'Personalized hardcover'}</span><strong>{selectedBook?.title || selectedTheme?.name || 'Loading your book…'}</strong><p>{selectedBook?.description || selectedTheme?.description || 'Getting your chosen story ready.'}</p><div className="selected-book-actions"><button type="button" className="change-book-button" onClick={() => setBookPickerOpen(true)} disabled={!themes.length}>Change book <ArrowIcon /></button>{selectedBook?.sampleAvailable && <button type="button" className="read-sample-button" onClick={() => setSampleOpen(true)}>Read the complete sample <ArrowIcon /></button>}</div></div>
            </div>
          </div>
          {personalizationFields.includes('photo') && <div className="photo-step">
            <div className="panel-heading"><span>2</span><div><strong>Choose their photo</strong><small>A clear, front-facing picture works best</small></div></div>
            <label className={`upload-box ${input.imageUrl ? 'has-image' : ''}`}>
              {input.imageUrl ? <><WatermarkedImage src={input.imageUrl} alt="Selected child" /><span className="photo-ready">✓ Photo ready</span></> : <><span className="upload-icon">＋</span><strong>{uploading ? 'Uploading your photo…' : 'Upload a favorite photo'}</strong><small>Tap to browse · JPG or PNG · up to 10 MB</small></>}
              <input type="file" accept="image/jpeg,image/png" onChange={event => pickPhoto(event.target.files?.[0])} disabled={uploading} />
              {uploading && <Spinner label="Uploading photo" />}
            </label>
            {input.imageUrl && <button className="quiet-button" onClick={() => setInput({ ...input, imageBase64: undefined, toapis: undefined, imageUuid: undefined, imageUrl: undefined, imagePath: undefined })}>Choose a different photo</button>}
          </div>}
        </div>
      </div>
      {selectedBook?.deliveryNote && <p className="holiday-delivery-note">{selectedBook.deliveryNote}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="studio-action studio-action-bar"><div><strong>Ready for a little magic?</strong><small>Your preview is free. No payment details needed.</small></div><button className="button" onClick={onCreate} disabled={uploading || !themes.length}>Create their preview <ArrowIcon /></button></div>
      {bookPickerOpen && <BookPickerModal themes={themes} selectedThemeId={input.themeId} onChoose={chooseTheme} onClose={() => setBookPickerOpen(false)} />}
      {sampleOpen && selectedBook?.sampleAvailable && <StudioSampleModal book={selectedBook} onClose={() => setSampleOpen(false)} />}
    </section>
  )
}

function StudioSampleModal({ book, onClose }: { book: StoreBook; onClose: () => void }) {
  const [characterId, setCharacterId] = useState<CatalogSampleCharacterId>(() => getSampleCharacters(book.id)[0])
  const [sample, setSample] = useState<CatalogSampleResult>()
  const [error, setError] = useState('')
  const [requestVersion, setRequestVersion] = useState(0)

  useEffect(() => {
    let active = true
    setSample(undefined)
    setError('')
    bookService.getCatalogSample(book.id, characterId)
      .then(result => active && setSample(result))
      .catch(() => active && setError('We couldn’t open this sample. Please try again.'))
    return () => { active = false }
  }, [book.id, characterId, requestVersion])

  return (
    <Modal onClose={onClose} labelledBy="studio-sample-title">
      <div className="studio-sample-modal">
        <div className="studio-sample-header">
          <div><h2 id="studio-sample-title">{book.title}</h2><p>Read all 26 pages without leaving your book setup.</p></div>
          <div className="studio-sample-character" role="group" aria-label="Choose a sample child">
            {getSampleCharacters(book.id).map(character => <button type="button" className={character === characterId ? 'active' : ''} aria-pressed={character === characterId} onClick={() => setCharacterId(character)} key={character}>Read with {character === 'lily' ? 'Lily' : 'Oliver'}</button>)}
          </div>
        </div>
        {error && <div className="sample-message" role="alert"><strong>The sample didn’t open.</strong><span>{error}</span><button type="button" onClick={() => setRequestVersion(version => version + 1)}>Try again</button></div>}
        {!sample && !error && <div className="studio-sample-loading"><Spinner label="Loading complete sample" /><span>Opening all 26 pages…</span></div>}
        {sample && <div className="studio-sample-pages"><figure className="studio-sample-cover"><img src={sample.coverUrl} alt={`${book.title} cover, personalized for ${sample.character.name}`} /><figcaption>Front cover</figcaption></figure>{sample.pages.map(page => <figure key={page.pageNumber}><img src={page.imageUrl} alt={page.title || `${book.title}, page ${page.pageNumber}`} loading={page.pageNumber <= 2 ? 'eager' : 'lazy'} decoding="async" /><figcaption><strong>{page.title || `Page ${page.pageNumber}`}</strong><span>{page.pageNumber} / 26</span></figcaption></figure>)}</div>}
        <div className="studio-sample-footer"><span>Want more room to read?</span><a className="button" href={sampleUrl(book.id, characterId)}>Open the full-page reader <ArrowIcon /></a></div>
      </div>
    </Modal>
  )
}

function BookPickerModal({ themes, selectedThemeId, onChoose, onClose }: { themes: BookThemeSummary[]; selectedThemeId: string; onChoose: (themeId: string) => void; onClose: () => void }) {
  const [category, setCategory] = useState<'All' | BookCategory>('All')
  const availableThemes = themes.filter(theme => category === 'All' || getStoreBook(theme.id)?.category === category)
  return (
    <Modal onClose={onClose} labelledBy="book-picker-title">
      <div className="book-picker-modal">
        <span className="eyebrow">Find the right story</span><h2 id="book-picker-title">Choose another book</h2>
        <div className="book-picker-tabs" role="group" aria-label="Filter books">{BOOK_CATEGORIES.map(item => <button type="button" className={category === item ? 'active' : ''} aria-pressed={category === item} onClick={() => setCategory(item)} key={item}>{item}</button>)}</div>
        <div className="book-picker-grid">{availableThemes.map(theme => {
          const book = getStoreBook(theme.id)
          const selected = theme.id === selectedThemeId
          return <button type="button" className={`book-picker-option ${selected ? 'selected' : ''}`} onClick={() => onChoose(theme.id)} key={theme.id}><img src={book?.image || `/images/theme-${theme.id}.jpg`} alt="" /><span><small>{book ? `${book.category} · ${book.age}` : 'Personalized book'}</small><strong>{book?.title || theme.name}</strong>{selected && <em>✓ Selected</em>}</span></button>
        })}</div>
      </div>
    </Modal>
  )
}

function Segmented({ options, value, onChange }: { options: string[][]; value: string; onChange: (value: string) => void }) {
  return <div className="segmented">{options.map(([key, label]) => <button type="button" className={value === key ? 'active' : ''} onClick={() => onChange(key)} key={key}>{label}</button>)}</div>
}

function GenerationProgress({ book, busy, startedAt, total }: { book: ArtBook; busy: LoadState; startedAt: number | null; total: number }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (busy !== 'loading' || !startedAt) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [busy, startedAt])
  const visiblePages = book.pages.filter(page => page.pageNumber <= total)
  const ready = visiblePages.filter(page => Boolean(page.generatedImageUrl)).length
  const creating = visiblePages.filter(page => !page.generatedImageUrl && page.generateState === 'loading').length
  const failed = visiblePages.filter(page => !page.generatedImageUrl && page.generateState === 'failed').length
  const waiting = Math.max(0, total - ready - creating - failed)
  const elapsed = startedAt ? Math.floor((now - startedAt) / 1000) : 0
  const complete = total > 0 && ready === total
  let heading = `${ready} of ${total} pages ready`
  let detail = 'Finished pages appear below as soon as they are saved.'
  if (complete) {
    heading = `All ${total} pages are ready`
    detail = 'Every illustration has been saved to your book.'
  } else if (failed && busy !== 'loading') {
    heading = `${failed} page${failed === 1 ? '' : 's'} need${failed === 1 ? 's' : ''} another try`
    detail = 'Everything already completed is safe. Retry only the pages marked below.'
  } else if (busy === 'loading' && elapsed < 20) {
    detail = 'We’re preparing the story and sending each page to the illustrator.'
  } else if (busy === 'loading' && elapsed < 45 && ready === 0) {
    detail = 'The first pages usually arrive in about 25–40 seconds. Keep this tab open.'
  } else if (busy === 'loading' && elapsed < 60) {
    detail = 'Finished pages appear below right away, so you can start looking through the story.'
  } else if (busy === 'loading' && elapsed < 90) {
    heading = `Still creating ${total - ready} page${total - ready === 1 ? '' : 's'}`
    detail = 'This batch is taking a little longer. We’re also checking the saved results in the background.'
  } else if (busy === 'loading') {
    heading = 'Taking longer than usual—but still working'
    detail = `We’re checking every remaining page. Your ${ready} completed page${ready === 1 ? ' is' : 's are'} already saved.`
  }
  return (
    <section className={`generation-progress ${failed ? 'has-errors' : ''}`} aria-live="polite" aria-atomic="true">
      <div className="generation-progress-copy"><div><strong>{heading}</strong><span>{detail}</span></div>{busy === 'loading' && <Spinner label="Book generation in progress" />}</div>
      <div className="generation-track" role="progressbar" aria-label="Book pages completed" aria-valuemin={0} aria-valuemax={total} aria-valuenow={ready}><span style={{ transform: `scaleX(${total ? ready / total : 0})` }} /></div>
      <dl className="generation-counts"><div><dt>Ready</dt><dd>{ready}</dd></div><div><dt>Creating</dt><dd>{creating}</dd></div><div><dt>Waiting</dt><dd>{waiting}</dd></div><div className={failed ? 'has-errors' : ''}><dt>Needs attention</dt><dd>{failed}</dd></div></dl>
    </section>
  )
}

function PreviewStep({ book, busy, error, generationStartedAt, onContinue }: { book: ArtBook; busy: LoadState; error: string; generationStartedAt: number | null; onContinue: () => void }) {
  const total = previewPageCount(book)
  const previewPages = book.pages.filter(page => page.pageNumber <= total)
  const ready = previewPages.filter(page => page.generatedImageUrl).length
  const failed = previewPages.filter(page => page.generateState === 'failed' && !page.generatedImageUrl).length
  return (
    <section className="studio-card preview-card">
      <div className="preview-hero">
        <div className="studio-title studio-title-left">
          <span className="eyebrow">Your free preview</span>
          <h1>{busy === 'loading' ? <>The magic is<br /><em>taking shape…</em></> : <>{book.metadata.name}&apos;s story<br /><em>has begun.</em></>}</h1>
          <p>{busy === 'loading' ? 'Keep this tab open while the first illustrations arrive. Each finished page will appear below right away.' : `Take a first look at ${book.metadata.name}'s ${book.metadata.themeName || 'book'}, then create the full story when you’re ready.`}</p>
        </div>
      </div>
      {total > 0 && <GenerationProgress book={book} busy={busy} startedAt={generationStartedAt} total={total} />}
      <div className="section-divider"><span>Your book at a glance</span><small>{ready} of {total || '—'} preview pages ready</small></div>
      <div className="preview-gallery">
        <div className="preview-spreads">{previewPages.map(page => <BookSpread key={page.pageNumber} page={page} coverImageUrl={book.cover.generatedImageUrl} />)}</div>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="studio-action studio-action-bar preview-action-card">
        <div><strong>{busy === 'loading' ? 'Your preview is on its way' : failed ? 'Your preview needs one more try' : 'Love what you see?'}</strong><small>{busy === 'loading' ? 'The button will be ready when both preview pages finish.' : 'No charge until you’re happy with the full book.'}</small></div>
        <button className="button" onClick={onContinue} disabled={busy === 'loading'}>{failed ? 'Retry preview' : 'Create all 26 pages'} <ArrowIcon /></button>
      </div>
    </section>
  )
}

function CustomizeStep({ book, busy, error, generationStartedAt, onChange, onRetry, onRetryPage, onCheckout }: { book: ArtBook; busy: LoadState; error: string; generationStartedAt: number | null; onChange: (page: ArtBookPage, changes: Partial<ArtBookPage>, makeCover?: boolean) => void; onRetry: () => void; onRetryPage: (pageNumber: number) => void; onCheckout: () => void }) {
  const complete = isBookComplete(book)
  const missingCount = pageCount(book) - generatedPageCount(book)
  const failedCount = book.pages.filter(page => !page.generatedImageUrl && page.generateState === 'failed').length
  return <section className="studio-card customize-card"><div className="customize-hero"><div className="studio-title studio-title-left"><span className="eyebrow">Your complete book</span><h1>{busy === 'loading' ? <>The story is<br /><em>coming together.</em></> : <>Make it perfectly<br /><em>theirs.</em></>}</h1><p>{busy === 'loading' ? 'Pages will appear as they finish. You can start enjoying the ready illustrations while the rest are created.' : 'Choose the cover you love and remove any illustration that doesn’t feel quite right.'}</p><div className="customize-summary"><span><strong>{PRINTED_BOOK_PAGE_COUNT}</strong> total pages</span></div></div><div className="customize-cover"><BookCover title={book.cover.coverTitleTop} imageUrl={book.cover.generatedImageUrl} compact /></div></div>{!complete && <GenerationProgress book={book} busy={busy} startedAt={generationStartedAt} total={pageCount(book)} />}<div className="customize-toolbar"><div><strong>Review every illustration</strong><small>{complete ? 'All illustrations are ready · changes save automatically' : busy === 'loading' ? 'Ready pages can be viewed now · keep this tab open' : `${missingCount} illustration${missingCount === 1 ? '' : 's'} still missing`}</small></div><span><b>♥</b> Set cover <i>×</i> Remove page</span></div><div className="customize-grid">{book.pages.map(page => <BookSpread key={page.pageNumber} page={page} coverImageUrl={book.cover.generatedImageUrl} editable={Boolean(page.generatedImageUrl)} onCover={() => onChange(page, {}, true)} onToggle={() => onChange(page, { isActive: page.isActive === false })} onRetry={page.generateState === 'failed' && busy !== 'loading' ? () => onRetryPage(page.pageNumber) : undefined} />)}</div>{busy === 'failed' && !complete && <div className="studio-action generation-recovery"><div><strong>{failedCount ? `${failedCount} page${failedCount === 1 ? '' : 's'} need another try` : 'Finish the remaining pages'}</strong><small>Your completed illustrations are saved and will not be regenerated.</small></div><button className="button" onClick={onRetry}>Retry all missing pages <ArrowIcon /></button></div>}{error && <p className="form-error" role="alert">{error}</p>}<div className="checkout-panel"><div><small>Your 26-page hardcover book</small><span>{complete ? 'Every illustration is ready · premium print · free shipping' : `Checkout unlocks when all 26 illustrations are ready`}</span></div><button className="button" onClick={onCheckout} disabled={!complete}>{complete ? 'Review order' : `${missingCount} page${missingCount === 1 ? '' : 's'} to finish`} <ArrowIcon /></button></div></section>
}

function CheckoutModal({ bookId, pageTotal, onClose }: { bookId?: string; pageTotal: number; onClose: () => void }) {
  const [state, setState] = useState<LoadState>('idle')
  async function checkout() {
    if (!bookId || state === 'loading') return
    setState('loading')
    try {
      const base = window.location.origin
      const result = await bookService.checkout({ photoBookId: bookId, successUrl: `${base}/order/?${constants.searchParams.orderId}={ORDER_ID}&${constants.searchParams.checkoutSessionId}={CHECKOUT_SESSION_ID}`, cancelUrl: `${base}/studio/?${constants.searchParams.photoBookId}=${bookId}` })
      window.location.assign(result.hostUrl)
    } catch (caught) { console.error(caught); setState('failed') }
  }
  return <Modal onClose={onClose} labelledBy="checkout-title"><span className="eyebrow">Almost on the bookshelf</span><h2 id="checkout-title">Your Lucie's Book</h2><ul className="order-benefits"><li>{pageTotal}-page personalized book</li><li>Premium color printing</li><li>20 × 20 cm hardcover</li><li>Free shipping</li></ul><div className="order-total"><span>Total</span><strong>$49.00</strong></div>{state === 'failed' && <p className="form-error">Checkout couldn’t open. Please try again.</p>}<button className="button button-full" onClick={checkout} disabled={!bookId || state === 'loading'}>{state === 'loading' ? <><Spinner /> Opening secure checkout…</> : <>Continue to secure checkout <ArrowIcon /></>}</button><small className="secure-note">Secure payment · satisfaction guarantee</small></Modal>
}
